/* AI 예제(공식 aiModel) S3 저장소 — 코드 예제와 같은 huenit-lab-code 버킷.
   정본 파서 = 앱 lib/features/ai/data/ai_example_catalog.dart(_listRemote) +
   lib/registry/{code_model_registry,s3_code_model_registry}.dart:
     · fetchOfficial(aiModel) = official/manifest.json 의 assets[] 에서 kind=='aiModel'
       만 필터 → 코드 예제(blocklyCode)와 manifest 공유. RMW 는 id 로만 갱신해 타 kind 보존.
     · 바이너리 객체 key = official/models/{id}/kmodel.kfpkg
       (앱 _resolveObjectKey 의 official+aiModel ext='kmodel.kfpkg'). ★ 'model.kfpkg' 아님 —
       앱 download() 가 kmodel.kfpkg 를 GET 하므로 다른 이름이면 404(예제 미로드).
     · meta.json = AssetMeta. 앱이 읽는 extras 키:
         extras.model_type            (AiExampleEntry.modelType)
         extras.i18n.<lang>.display_name  (AssetMetaL10n.displayNameFor — 카드 다국어 표시명)
   kfpkg sanity(ZIP 매직 PK\x03\x04 + 최소 크기) 로 깨진 모델 업로드를 최상류 차단해
   사용자 카메라 벽돌을 예방한다(앱측 W146 검증과 이중 방어).

   s3.js(동결 시그니처)만 사용 — 코드 예제 버킷과 동일 클라이언트. */
import { getJsonAnon, putObject, deleteObject } from '../../api/s3.js';
import { buildManifest } from '../../data/mappers.js';
import { CODE_BUCKET_URL } from '../../config/env.js';
import { bumpExamplesVersion } from '../examples/repo.js';   // 같은 official/manifest.json → 동일 버전 신호

export const OFFICIAL_MANIFEST_KEY = 'official/manifest.json';
export const AI_KIND = 'aiModel';

const KFPKG_MAGIC = [0x50, 0x4b, 0x03, 0x04];   // 'PK\x03\x04' = ZIP local file header
const KFPKG_MIN_BYTES = 1024;                    // 빈/잘린 kfpkg 차단 하한(정상 kmodel ≫ 이 값)

const manifestUrl = (key) => (CODE_BUCKET_URL || '') + '/' + key;
const modelKey = (id) => `official/models/${id}/kmodel.kfpkg`;
const metaKey = (id) => `official/models/${id}/meta.json`;

/* 모델 파일(kmodel.kfpkg) 공개 URL (다운로드용) — official/* 은 public-read. */
export function modelFileUrl(id) {
  return (CODE_BUCKET_URL || '') + '/' + modelKey(id);
}

/* assetId = 클라 생성 UUIDv4 (코드 예제와 동일 규약). */
export function newModelId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/* sha256 hex — Web Crypto. buffer = ArrayBuffer. */
async function sha256Hex(buffer) {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/* kfpkg(=ZIP) 1차 무결성 — 매직바이트 + 최소 크기. 깨진 모델이 사용자 카메라로
   흘러가 벽돌 일으키는 최상류 차단. 실패 시 throw(업로드 중단). */
export function validateKfpkg(buffer) {
  const bytes = new Uint8Array(buffer);
  if (bytes.length < KFPKG_MIN_BYTES) {
    throw new Error(`파일이 너무 작습니다(${bytes.length}B) — 손상되었거나 빈 kfpkg 입니다.`);
  }
  for (let i = 0; i < KFPKG_MAGIC.length; i++) {
    if (bytes[i] !== KFPKG_MAGIC[i]) {
      throw new Error('kfpkg(ZIP) 형식이 아닙니다 — K210 .kfpkg 모델 파일이 맞는지 확인하세요.');
    }
  }
}

/* official/manifest.json 읽기. 없음(404/403) → 빈 배열. 형식 깨짐 → throw
   (orphan 쓰기 전 fail-fast). 코드 예제(examples/repo.js)와 같은 파일을 공유한다. */
async function readAssets() {
  let json;
  try {
    json = await getJsonAnon(manifestUrl(OFFICIAL_MANIFEST_KEY));
  } catch (e) {
    if (e && (e.status === 404 || e.status === 403)) return [];
    throw new Error(`목록(manifest)을 읽지 못했습니다: ${e && e.message ? e.message : e}`);
  }
  if (!json || !Array.isArray(json.assets)) {
    throw new Error('official/manifest.json 형식이 올바르지 않습니다 (assets 배열 없음) — 덮어쓰기 전 중단합니다.');
  }
  return json.assets;
}

/* MLText i18n({ko:'..',en:'..'}) → 앱 AssetMetaL10n 이 읽는 형태
   extras.i18n.<lang>.display_name (빈 값 제외). */
function toI18n(displayNameI18n) {
  const out = {};
  for (const [lang, name] of Object.entries(displayNameI18n || {})) {
    if (typeof name === 'string' && name.trim()) out[lang] = { display_name: name.trim() };
  }
  return out;
}

/* 목록 — official/manifest.json 의 aiModel 항목만(코드 예제와 분리). 최신순. */
export async function listAiModels() {
  const assets = await readAssets();
  return assets
    .filter((a) => a && a.kind === AI_KIND)
    .map((m) => {
      const extras = m.extras || {};
      const i18n = extras.i18n || {};
      const displayNameI18n = {};
      for (const [lang, node] of Object.entries(i18n)) {
        if (node && typeof node.display_name === 'string') displayNameI18n[lang] = node.display_name;
      }
      return {
        id: m.id,
        displayName: m.display_name || m.id,
        displayNameI18n,
        modelType: extras.model_type || '',
        sha256: m.sha256 || '',
        sizeBytes: m.size_bytes || 0,
        uploadedAt: m.uploaded_at || '',
        _meta: m,
      };
    })
    .sort((a, b) => String(b.uploadedAt).localeCompare(String(a.uploadedAt)));
}

/* 등록/수정 (RMW). model = { id, displayNameI18n:{ko,en,..}, modelType }.
   fileBuffer(ArrayBuffer) 있으면 새 .kfpkg 업로드(신규 시 필수), 없으면 메타만 갱신.
   owner = { email, defaultLang }. onStep(label) = 진행 표시 콜백. */
export async function saveAiModel(getCredentials, { model, fileBuffer, owner = {}, onStep }) {
  const id = model.id;
  const defaultLang = owner.defaultLang || 'ko';
  const step = (s) => { if (onStep) onStep(s); };
  const names = model.displayNameI18n || {};
  const i18n = toI18n(names);
  const displayName =
    (names[defaultLang] && names[defaultLang].trim()) ||
    Object.values(names).map((s) => (s || '').trim()).find(Boolean) ||
    id;
  const modelType = (model.modelType || '').trim();

  step('목록 확인 중…');
  const assets = await readAssets();                     // 쓰기 전 read = orphan 방지
  const prev = assets.find((a) => a.id === id && a.kind === AI_KIND) || null;

  let sha256;
  let sizeBytes;
  if (fileBuffer) {
    validateKfpkg(fileBuffer);                           // 깨진 모델 최상류 차단(throw)
    sizeBytes = fileBuffer.byteLength;
    step('체크섬 계산 중…');
    sha256 = await sha256Hex(fileBuffer);
    step('모델 파일 업로드 중…');
    await putObject(getCredentials, modelKey(id), new Uint8Array(fileBuffer), 'application/zip');
  } else if (prev) {
    sha256 = prev.sha256;
    sizeBytes = prev.size_bytes;
  } else {
    throw new Error('모델 파일(.kfpkg)을 업로드하세요.');
  }

  const extras = { model_type: modelType };
  if (Object.keys(i18n).length) extras.i18n = i18n;
  // 종류 다국어 라벨(26.07.04-admin §4) — 앱 카드/필터 칩이 raw value 대신 admin 라벨 표시.
  // 라벨 미전달(종류가 목록에서 삭제된 경우 등)이면 같은 value 의 기존 라벨을 보존.
  const typeI18n = model.modelTypeI18n && typeof model.modelTypeI18n === 'object'
    ? Object.fromEntries(Object.entries(model.modelTypeI18n).filter(([, v]) => typeof v === 'string' && v.trim()))
    : null;
  if (typeI18n && Object.keys(typeI18n).length) {
    extras.model_type_i18n = typeI18n;
  } else if (prev && prev.extras && prev.extras.model_type === modelType && prev.extras.model_type_i18n) {
    extras.model_type_i18n = prev.extras.model_type_i18n;
  }

  const meta = {
    id,
    kind: AI_KIND,
    display_name: displayName,
    ownership: 'official',
    visibility: 'public',
    size_bytes: sizeBytes,
    sha256,
    uploaded_at: prev ? prev.uploaded_at : new Date().toISOString(),
    extras,
  };
  if (owner.email) meta.owner_email = owner.email;

  step('메타데이터 저장 중…');
  await putObject(getCredentials, metaKey(id), JSON.stringify(meta), 'application/json');

  step('목록(manifest) 갱신 중…');
  const fresh = await readAssets();                      // RMW: 쓰기 직전 재읽기로 경쟁 윈도 최소화
  const next = fresh.filter((a) => a.id !== id);         // 타 kind(blocklyCode) 포함 전부 보존
  next.push(meta);
  await putObject(getCredentials, OFFICIAL_MANIFEST_KEY, JSON.stringify(buildManifest(next)), 'application/json');
  step('버전 신호 발행 중…');
  await bumpExamplesVersion(getCredentials);
  return meta;
}

/* 삭제 (RMW) — 모델 객체(kmodel.kfpkg) + meta.json + manifest 엔트리 제거. */
export async function deleteAiModel(getCredentials, id, onStep) {
  const step = (s) => { if (onStep) onStep(s); };
  step('목록 확인 중…');
  const assets = await readAssets();                     // 쓰기 전 read = orphan 방지
  step('파일 삭제 중…');
  await deleteObject(getCredentials, modelKey(id));
  await deleteObject(getCredentials, metaKey(id));
  step('목록(manifest) 갱신 중…');
  const next = assets.filter((a) => a.id !== id);
  await putObject(getCredentials, OFFICIAL_MANIFEST_KEY, JSON.stringify(buildManifest(next)), 'application/json');
  step('버전 신호 발행 중…');
  await bumpExamplesVersion(getCredentials);
}
